import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import orgDataService from "services/orgDataService";
import employeeService from "services/employeeService";
import formsService from "services/formsService";
import { Hub } from "common/models";
import { Employee } from "employee/models";
import { toast } from "react-toastify";
import * as Yup from "yup";

interface FormData {
  locationId: number | null;
  employeeId: number | null;
  tripShiftNumber: string;
  currentHours: string;
  timeRequested: string;
}

interface FormErrors {
  locationId?: string;
  employeeId?: string;
  tripShiftNumber?: string;
  currentHours?: string;
  timeRequested?: string;
}

const validationSchema = Yup.object().shape({
  locationId: Yup.number().required("Location is required").nullable(),
  employeeId: Yup.number().required("Employee is required").nullable(),
  tripShiftNumber: Yup.string().required("Trip/Shift # is required"),
  currentHours: Yup.string().required("Current hours is required"),
  timeRequested: Yup.string().required("Time requested is required"),
});

const OvertimeRequest = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Hub[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<FormData>({
    locationId: null,
    employeeId: null,
    tripShiftNumber: "",
    currentHours: "",
    timeRequested: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.locationId) {
      loadEmployeesByHub(formData.locationId);
    } else {
      setEmployees([]);
    }
  }, [formData.locationId]);

  const loadData = async () => {
    try {
      const hubsData = await orgDataService.getAllHubs();
      setLocations(hubsData);
    } catch (error) {
      toast.error("Failed to load form data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmployeesByHub = async (hubId: number) => {
    setIsLoadingEmployees(true);
    try {
      const result = await employeeService.searchEmployees({
        hubId: hubId,
        status: "activeOnly",
        pagedSearchOptions: { offset: 0, pageSize: 500 },
        sortName: "lastName",
        sortAsc: true,
      } as any);
      setEmployees(result.results || []);
    } catch (error) {
      toast.error("Failed to load employees");
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const validate = (): boolean => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const validation = error as Yup.ValidationError;
      const newErrors: FormErrors = {};
      validation.inner.forEach((err) => {
        if (err.path) {
          newErrors[err.path as keyof FormErrors] = err.message;
        }
      });
      setErrors(newErrors);
      return false;
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!validate()) return;

    const selectedLocation = locations.find((l) => l.id === formData.locationId);
    const selectedEmployee = employees.find((e) => e.id === formData.employeeId);

    if (!selectedLocation || !selectedEmployee) {
      toast.error("Please select a valid location and employee");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await formsService.submitOvertimeRequest({
        locationId: formData.locationId!,
        locationName: selectedLocation.name,
        employeeId: formData.employeeId!,
        employeeName: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
        employeeNumber: selectedEmployee.employeeNumber,
        tripShiftNumber: formData.tripShiftNumber,
        currentHours: formData.currentHours,
        timeRequested: formData.timeRequested,
      });

      if (response.success) {
        toast.success("Overtime request submitted successfully");
        navigate("/forms");
      } else {
        toast.error(response.message || "Failed to submit overtime request");
      }
    } catch (error) {
      toast.error("Failed to submit overtime request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    if (field === "locationId") {
      setFormData({ ...formData, [field]: value, employeeId: null });
      setErrors({ ...errors, locationId: undefined, employeeId: undefined });
    } else {
      setFormData({ ...formData, [field]: value });
      if (errors[field]) {
        setErrors({ ...errors, [field]: undefined });
      }
    }
  };

  if (isLoading) {
    return (
      <MDBox p={3}>
        <MDTypography>Loading...</MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">
            Overtime Request
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Submit overtime authorization request
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
          Back to Forms
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.locationId}>
                <InputLabel id="location-label">Location *</InputLabel>
                <Select
                  labelId="location-label"
                  id="locationId"
                  value={formData.locationId || ""}
                  label="Location *"
                  onChange={(e) => handleChange("locationId", e.target.value)}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationId && <FormHelperText>{errors.locationId}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={!!errors.employeeId}
                disabled={!formData.locationId || isLoadingEmployees}
              >
                <InputLabel id="employee-label">
                  {isLoadingEmployees ? "Loading employees..." : "Employee Name *"}
                </InputLabel>
                <Select
                  labelId="employee-label"
                  id="employeeId"
                  value={formData.employeeId || ""}
                  label={isLoadingEmployees ? "Loading employees..." : "Employee Name *"}
                  onChange={(e) => handleChange("employeeId", e.target.value)}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.lastName}, {employee.firstName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.employeeId && <FormHelperText>{errors.employeeId}</FormHelperText>}
                {!formData.locationId && !errors.employeeId && (
                  <FormHelperText>Select a location first</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3} mt={0}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Trip / Shift # *"
                value={formData.tripShiftNumber}
                onChange={(e) => handleChange("tripShiftNumber", e.target.value)}
                error={!!errors.tripShiftNumber}
                helperText={errors.tripShiftNumber}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Current Hours *"
                value={formData.currentHours}
                onChange={(e) => handleChange("currentHours", e.target.value)}
                error={!!errors.currentHours}
                helperText={errors.currentHours}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time Requested (Hours) *"
                value={formData.timeRequested}
                onChange={(e) => handleChange("timeRequested", e.target.value)}
                error={!!errors.timeRequested}
                helperText={errors.timeRequested}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
            <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
              Cancel
            </MDButton>
            <MDButton variant="contained" color="dark" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </MDButton>
          </Box>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default OvertimeRequest;
